import { useEffect, useMemo, useState } from "react"

import { XIcon, MessageCircleQuestion } from "lucide-react"
import { Select, SelectMulti, Input, DefaultPopover, Separator, List, ToggleOptionGroup, ToggleOptionGroupItem, DefaultTooltip, ListItem } from "twyne-new.ui"

import { TailwindSandboxPure } from "@/modules/cms/views/post/components/builder/Content/TailwindSandbox"

import { parseTimingFunction } from "../../utils"
import ApplyButtonPopover from "../components/ApplyButtonPopover"
import { CubicBezierPopover, CubicBezierToggle, useCubicBezier } from "../components/CubicBezier"

import ConfigInputs from "./ConfigInputs"

const Poppover = ({ children, onApply, transition, onChange, transitions, index }) => {
  const [open, setOpen] = useState(false)
  const [displayedInputs, setDisplayedInputs] = useState()

  const { type, duration, ease, delay, behavior, property } = transition
  const transitionProperty = useMemo(() => !property ? [] : Array.isArray(property) ? property : [property], [property])

  const blockBehavior = index > 0 && transitions.find(item => item.behavior)?.behavior

  const handleClose = () => {
    setOpen(false)
    onChange()
  }

  return <>
    <DefaultPopover
      trigger={children}
      triggerProps={{ asChild: true }}
      contentProps={{ className: 'w-[500px]', side: 'left', align: 'center' }}
      wrapperProps={{ open: open, onOpenChange: open => open ? setOpen(open) : handleClose() }}
    >
      <div className="w-full">
        <div className='flex justify-between items-center'>
          <div className='flex gap-2 items-center '>
            Transition
            <ConfigInputs transition={transition} displayedInputs={displayedInputs} onChange={setDisplayedInputs} />
          </div>
          <XIcon size='16' className='cursor-pointer' onClick={() => setOpen(false)} />
        </div>
        <Separator className='m-0 my-2' />
        <Inputs displayedInputs={displayedInputs} transition={transition} onChange={onChange} blockBehavior={blockBehavior} />
      </div>

      {type !== 'arbitrary' && <>
        <Separator className='m-0 my-2' />
        Preview
        <div className='my-2'>
          <Preview
            transitions={transitionProperty}
            duration={duration}
            delay={delay}
            ease={ease}
            behavior={behavior}
            key={transitionProperty + duration + delay + ease + behavior}
          />
        </div>
      </>}
      <ApplyButtonPopover
        className='mt-2'
        onApply={onApply}
        onCancel={handleClose}
        name='transition'
        value={transitions}
      />
    </DefaultPopover>
  </>
}

const Inputs = ({ displayedInputs, transition = {}, onChange, blockBehavior }) => {
  const { type, property, ease, duration, delay, behavior } = transition

  const handleChangePropertyArbitrary = value => {
    const properties = value.map(item => item?.value)
    onChange({ property: properties.length ? properties : undefined })
  }

  const handleSetType = value => {
    onChange({ type: value, property: null })
  }

  if (!displayedInputs) {
    return null
  }

  return <List className='p-0'>
    <div className='flex flex-col gap-2'>

      <PropertyItem show>
        <div className='col-span-2'>Type</div>
        <div className='col-span-3'>
          <ToggleOptionGroup size='lg' value={type || 'predefined'} type='single' onValueChange={value => value && handleSetType(value)}>
            <ToggleOptionGroupItem value='predefined'>Predefined</ToggleOptionGroupItem>
            <ToggleOptionGroupItem value='arbitrary'>Arbitrary</ToggleOptionGroupItem>
          </ToggleOptionGroup>
        </div>
      </PropertyItem>

      <PropertyItem show={displayedInputs.includes('property')}>
        <div className='col-span-2'>Property</div>
        <div className='col-span-3'>
          {type !== 'arbitrary' ? <>
            <Select
              value={property}
              onChange={option => onChange({ property: option?.value })}
              options={TRANSITIONS_PREDEFINED}
              clearable
            />
          </> : <>
            <SelectMulti
              value={property}
              onChange={handleChangePropertyArbitrary}
              options={TRANSITIONS_ARBITRARY}
              clearable
              creatable
            />
          </>}
        </div>
      </PropertyItem>

      <TimingFunction displayedInputs={displayedInputs} onChange={onChange} ease={ease} duration={duration} />

      <PropertyItem show={displayedInputs.includes('duration')}>
        <div className='col-span-2'>Duration (ms)</div>
        <div className='col-span-3'>
          <Input
            type='number'
            value={duration}
            onChange={e => onChange({ duration: e.target.value })}
          />
        </div>
      </PropertyItem>

      <PropertyItem show={displayedInputs.includes('delay')}>
        <div className='col-span-2'>Delay (ms)</div>
        <div className='col-span-3'>
          <Input
            type='number'
            value={delay}
            onChange={e => onChange({ delay: e.target.value })}
          />
        </div>
      </PropertyItem>

      <PropertyItem show={displayedInputs.includes('behavior')}>
        <div className='col-span-2 flex items-center gap-1'>
          Behavior
          <DefaultTooltip content='Use discrete when a change should happen all at once, not gradually.
          For example: showing or hiding an element (hidden/block) or changing its position (static/relative/absolute).
          It does not affect colors, opacity, or smooth movements — only properties thtat do not have intermediate values.'>
            <MessageCircleQuestion size='12' className='ml-1' />
          </DefaultTooltip>
        </div>

        <DefaultTooltip content={blockBehavior && 'Each element must have just one behavior'} triggerProps={{ asChild: true }}>
          <div className='col-span-3'>
            <ToggleOptionGroup size='lg' value={blockBehavior || behavior} type='single' onValueChange={value => onChange({ behavior: value })}>
              <ToggleOptionGroupItem disabled={blockBehavior} value='transition-normal'>Normal</ToggleOptionGroupItem>
              <ToggleOptionGroupItem disabled={blockBehavior} value='transition-discrete'>Discrete</ToggleOptionGroupItem>
            </ToggleOptionGroup>
          </div>
        </DefaultTooltip>

      </PropertyItem>
    </div>
  </List>
}

const TimingFunction = ({ displayedInputs, onChange, ease, duration }) => {
  const { cubic, onChangeCubic } = useCubicBezier({ value: ease, easeOptions: EASE_OPTIONS })

  const handleChangeCubicToggle = value => {
    if (value === 'cubic') {
      onChange({ ease: [{ x: 0, y: 0 }, { x: 0.42, y: 0 }, { x: 1, y: 1 }, { x: 1, y: 1 }] })
    } else {
      onChange({ ease: undefined })
    }

    onChangeCubic(value === 'cubic')
  }

  return <>
    <PropertyItem show={displayedInputs.includes('timing function')}>
      <div className='col-span-2'>Timing Function</div>
      <div className='col-span-3 flex items-center gap-2'>
        {cubic ? <>
          <CubicBezierPopover
            inputProps={{ variant: 'accent' }}
            duration={duration}
            value={ease}
            onChange={ease => onChange({ ease })}
          />
        </> : <>
          <Select
            value={ease}
            onChange={option => onChange({ ease: option?.value })}
            options={EASE_OPTIONS}
            clearable
          />
        </>}
        <CubicBezierToggle value={cubic} onChange={handleChangeCubicToggle} />
      </div>
    </PropertyItem>
  </>
}

const Preview = ({ transitions, duration, delay, ease = 'ease-linear', behavior = '' }) => {
  const [effectClasses, setEffectClasses] = useState({ effect: 'default', transition: '', default: '' })

  const classes = useMemo(() => {
    const transitionsClasses = transitions.map(transition => TRANSITION_CLASSES[transition] || { default: '', transition: '' })
    const defaultClasses = transitionsClasses.map(transition => transition.default).join(' ')
    const transitionClasses = transitionsClasses.map(transition => transition.transition).join(' ')

    return { default: defaultClasses, transition: transitionClasses }
  }, [transitions])

  useEffect(() => {
    let effect = true
    const intervalTime = Number(duration) + 500

    const timer = setInterval(() => {
      setEffectClasses({ effect: effect ? 'transition' : 'default', transition: classes.transition, default: classes.default })

      effect = !effect
    }, intervalTime)

    return () => clearInterval(timer)
  }, [duration, classes])

  const defaultClasses = 'w-fit rounded-xs py-2 px-3 text-gray-100 bg-blue-500 cursor-pointer text-xs'
  const easeClass = `ease-[${parseTimingFunction(ease)}]`

  return <TailwindSandboxPure className='w-full h-[50px]'>
    <div className="flex items-center justify-center flex-col gap-2 h-full">
      <div
        className={`${defaultClasses} ${easeClass} ${behavior} ${effectClasses[effectClasses.effect]} transition-all`}
        style={{ transitionDuration: `${duration}ms`, transitionDelay: `${delay}ms` }}
      >
        Button
      </div>
    </div>
  </TailwindSandboxPure>
}

const PropertyItem = ({ children, show }) => <>
  <ListItem className={show ? 'block' : 'hidden'}>
    <div className='grid grid-cols-5 gap-2 items-center w-full'>
      {children}
    </div>
  </ListItem>
</>

const TRANSITION_CLASSES = {
  all: {
    default: 'opacity-100 scale-100 py-2 px-3 bg-blue-500',
    transition: 'opacity-50 scale-110 py-4 px-5 bg-red-500',
  },
  opacity: {
    default: 'opacity-100',
    transition: 'opacity-10',
  },
  colors: {
    default: 'bg-blue-500',
    transition: 'bg-red-500',
  },
  shadow: {
    default: 'shadow-none',
    transition: 'shadow-md shadow-sky-400',
  },
  transform: {
    default: 'scale-100',
    transition: 'scale-120',
  },
}

const TRANSITIONS_PREDEFINED = [
  { value: 'all', label: 'All' },
  { value: 'colors', label: 'Colors' },
  { value: 'opacity', label: 'Opacity' },
  { value: 'shadow', label: 'Shadow' },
  { value: 'transform', label: 'Transform' },
]

export const EASE_OPTIONS = [
  { value: 'ease-linear', label: 'Linear' },
  { value: 'ease-in', label: 'In' },
  { value: 'ease-out', label: 'Out' },
  { value: 'ease-in-out', label: 'In out' },
  { value: 'ease-initial', label: 'Initial' },
]

const TRANSITIONS_ARBITRARY = [
  { value: 'width', label: 'Width' },
  { value: 'height', label: 'Height' },
  { value: 'min-width', label: 'Min Width' },
  { value: 'max-width', label: 'Max Width' },
  { value: 'min-height', label: 'Min Height' },
  { value: 'max-height', label: 'Max Height' },
  { value: 'padding', label: 'Padding' },
  { value: 'margin', label: 'Margin' },
  { value: 'position', label: 'Position' },
  { value: 'top', label: 'Top' },
  { value: 'right', label: 'Right' },
  { value: 'bottom', label: 'Bottom' },
  { value: 'left', label: 'Left' },

  { value: 'background', label: 'Background' },
  { value: 'background-color', label: 'Background Color' },
  { value: 'background-image', label: 'Background Image' },

  { value: 'shadow', label: 'Shadow' },
  { value: 'text-shadow', label: 'Text Shadow' },
  { value: 'text-align', label: 'Text Align' },
  { value: 'letter-spacing', label: 'Letter Spacing' },
  { value: 'line-height', label: 'Line Height' },
  { value: 'font-size', label: 'Font Size' },
  { value: 'font-weight', label: 'Font Weight' },
  { value: 'font-family', label: 'Font Family' },
  { value: 'text-decoration-line', label: 'Text Decoration Line' },
  { value: 'text-decoration-color', label: 'Text Decoration Color' },

  { value: 'border-width', label: 'Border Width' },
  { value: 'border-radius', label: 'Border Radius' },
  { value: 'border-color', label: 'Border Color' },
  { value: 'border-style', label: 'Border Style' },

  { value: 'ring', label: 'Ring' },
  { value: 'ring-width', label: 'Ring Width' },
  { value: 'ring-color', label: 'Ring Color' },
  { value: 'ring-offset', label: 'Ring Offset' },
  { value: 'ring-offset-width', label: 'Ring Offset Width' },
  { value: 'ring-offset-color', label: 'Ring Offset Color' },

  { value: 'outline', label: 'Outline' },
  { value: 'outline-width', label: 'Outline Width' },
  { value: 'outline-style', label: 'Outline Style' },
  { value: 'outline-color', label: 'Outline Color' },
  { value: 'outline-offset', label: 'Outline Offset' },
  { value: 'outline-offset-width', label: 'Outline Offset Width' },
  { value: 'outline-offset-style', label: 'Outline Offset Style' },

  { value: 'opacity', label: 'Opacity' },

  { value: 'shadow', label: 'Shadow' },
]

export default Poppover
